import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Separator } from '../ui/separator';
import {
  Sheet,
  SheetContent,
} from '../ui/sheet';
import {
  Menu,
  X,
} from 'lucide-react';
import ChatSidebar from './ChatSidebar';
import MessageStream from './MessageStream';
import EnhancedInputArea from './EnhancedInputArea';
import { useChatSession } from '../../hooks/useChatSession';

const SIDEBAR_WIDTH = 320;

const FullScreenChat = ({
  sessionId,
  sessionsList,
  onSessionSwitch,
  isMobile
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [notification, setNotification] = useState(null);
  const messagesEndRef = useRef(null);

  const {
    messages,
    streamMessage,
    newConversation,
    clearSession,
    isLoading,
    error,
    isSessionReady,
  } = useChatSession({
    autoLoadHistory: false, // ✅ Don't auto-load here since AgentChatPage handles it
    onError: (err) => {
      setNotification({
        type: 'error',
        message: `Error: ${err.message}`,
      });
    },
  });

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, streamingMessage]);

  // Handle sidebar toggle
  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Handle message sending with streaming support
  const handleSendMessage = async (messageContent, options = {}) => {
    if (!messageContent.trim() || !isSessionReady) return;

    try {
      setIsStreaming(true);
      setStreamingMessage(''); // Reset but don't accumulate here

      // Start streaming - the hook handles all message state
      await streamMessage(
        messageContent,
        (chunk) => {
          // Don't accumulate here - let the hook handle it
          // This callback can be used for other UI feedback if needed
        },
        options
      );

    } catch (err) {
      console.error('Failed to send message:', err);
      setNotification({
        type: 'error',
        message: 'Failed to send message. Please try again.',
      });
    } finally {
      setIsStreaming(false);
      setStreamingMessage(''); // Clear any local state
    }
  };

  // Handle session management
  const handleNewConversation = async () => {
    try {
      await newConversation();
      setNotification({
        type: 'success',
        message: '新对话已开始',
      });
    } catch (err) {
      console.error('Failed to start new conversation:', err);
    }
  };

  const handleClearSession = async () => {
    try {
      await clearSession();
      setNotification({
        type: 'info',
        message: '聊天记录已清除',
      });
    } catch (err) {
      console.error('Failed to clear session:', err);
    }
  };

  const handleCloseNotification = () => {
    setNotification(null);
  };

  // Auto-dismiss notification
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Sidebar component
  const sidebarContent = (
    <ChatSidebar
      sessionsList={sessionsList}
      currentSessionId={sessionId}
      onSessionSwitch={onSessionSwitch}
      onNewConversation={handleNewConversation}
      onClearSession={handleClearSession}
      width={SIDEBAR_WIDTH}
    />
  );

  return (
    <div className="h-full flex bg-muted">
      {/* Desktop Sidebar */}
      {!isMobile && (
        <div
          className={`flex-shrink-0 border-r bg-card transition-all duration-300 ${
            sidebarOpen ? 'w-80' : 'w-0'
          } overflow-hidden`}
        >
          {sidebarContent}
        </div>
      )}

      {/* Mobile Sidebar (Sheet) */}
      {isMobile && (
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetContent side="left" className="w-80 p-0">
            <div className="flex justify-end p-2">
              <Button variant="ghost" size="icon" onClick={handleSidebarToggle}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <Separator />
            {sidebarContent}
          </SheetContent>
        </Sheet>
      )}

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col h-full min-w-0 transition-all duration-300">
        {/* Sidebar Toggle Button (Mobile) */}
        {isMobile && (
          <div className="flex items-center p-2 border-b bg-card">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleSidebarToggle}
              className="mr-2"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>
        )}

        {/* Sidebar Toggle Button (Desktop) */}
        {!isMobile && (
          <div
            className="absolute top-4 z-50 transition-all duration-300"
            style={{ left: sidebarOpen ? SIDEBAR_WIDTH + 16 : 16 }}
          >
            <Button
              variant="outline"
              size="icon"
              onClick={handleSidebarToggle}
              className="bg-card shadow-md hover:bg-muted"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>
        )}

        {/* Messages Area */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          <div
            className={`flex-1 overflow-hidden flex flex-col min-h-0 transition-opacity duration-300 ${
              isSessionReady ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <MessageStream
              messages={messages}
              streamingMessage={streamingMessage}
              isStreaming={isStreaming}
              isLoading={isLoading}
              error={error}
            />
            <div ref={messagesEndRef} />
          </div>
        </div>

        <Separator />

        {/* Input Area */}
        <div className="p-4 bg-card border-t">
          <EnhancedInputArea
            onSendMessage={handleSendMessage}
            disabled={!isSessionReady || isStreaming}
            isLoading={isLoading}
            placeholder={
              isStreaming
                ? 'AI正在思考...'
                : '请问您有什么问题...'
            }
          />
        </div>
      </div>

      {/* Notifications */}
      {notification && (
        <div className="fixed bottom-4 right-4 z-50">
          <Alert
            variant={notification.type === 'error' ? 'destructive' : 'default'}
            className={`w-80 shadow-lg ${
              notification.type === 'success' ? 'border-green-500 bg-success/10' :
              notification.type === 'info' ? 'border-blue-500 bg-primary/10' : ''
            }`}
          >
            <AlertDescription className="flex items-center justify-between">
              <span>{notification.message}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCloseNotification}
                className="h-6 w-6 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      )}
    </div>
  );
};

export default FullScreenChat;
